import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { FormikErrors, useFormik } from 'formik';
import {
  GRIEVANCE_MILESTONES_CODES,
  GRIEVANCE_STATUSES,
  GrievanceLogbookItem,
  GrievanceMilestone,
  GrievancePhaseDataApply,
} from '../../../../services/types/Grievance';
import UIKitButton from '../../../../shared/UIKITComponents/Button';
import UIKitDatepicker from '../../../../shared/UIKITComponents/DatepickerCustom';
import UIKitDropDown from '../../../../shared/UIKITComponents/DropDown';
import { MilestonesRowProps } from './MilestonesRowProps';
import Context from '../context/DetailsContext';
import MainContext from '../../../App/MainProvider/context';
import { GRIEVANCE_PHASES } from '../../constants';
import { OptionValue } from '../../../../services/types/Misc';

interface FormModel extends GrievancePhaseDataApply {
  phase: 'Applicability' | 'A&R' | 'Monitoring' | 'Closed' | 'N/A';
  phaseId: number;
  isCurrent: boolean;
  milestones: GrievanceMilestone[];
  logbookItems: GrievanceLogbookItem[];
}

const BOOLEAN_OPTIONS = [
  { id: 1, text: 'Yes', value: 'true' },
  { id: 2, text: 'No', value: 'false' },
];

const boolToOptionId = (value: unknown) => {
  if (value === true) {
    return 1;
  }
  if (value === false) {
    return 2;
  }
  return 0;
};

// `errors` es el acumulador que la propia funcion validate() de Formik espera mutar y
// devolver (patron estandar de Formik) - no-param-reassign no aplica a este caso.
const setMilestoneError = (errors: FormikErrors<FormModel>, index: number, fieldError: string) => {
  if (!errors.milestones) {
    // eslint-disable-next-line no-param-reassign
    errors.milestones = [];
  }
  if (!Array.isArray(errors.milestones)) {
    // eslint-disable-next-line no-param-reassign
    errors.milestones = [];
  }
  const errorArray = errors.milestones as FormikErrors<GrievanceMilestone>[];
  errorArray[index] = {
    ...errorArray[index],
    date: fieldError,
  };
};

const ClosedMilestoneRow = (props: MilestonesRowProps) => {
  const { isEditable, phase, onPhaseApply, registrationDate } = props;
  const { user, inSuperUserMode } = useContext(MainContext);
  const { grievancePhaseData, lookupsClosureTypes, grievanceNumber, phases } = useContext(Context);
  const { t } = useTranslation();

  const isActivePhase = useMemo(
    () => grievancePhaseData.currentPhaseName === GRIEVANCE_PHASES.CLOSED,
    [grievancePhaseData.currentPhaseName]
  );

  const alwaysEditable = useMemo(
    () =>
      grievancePhaseData.currentPhaseName === GRIEVANCE_PHASES.CLOSED &&
      grievancePhaseData.currentStatus !== GRIEVANCE_STATUSES.NOT_APPLICABLE &&
      (inSuperUserMode() || user.grievanceRole === 'GPC' || user.grievanceRole === 'GPT'),
    [grievancePhaseData, inSuperUserMode, user.grievanceRole]
  );

  const canEditAuthClousureDate = useMemo(
    () =>
      isActivePhase &&
      grievancePhaseData.currentStatus === GRIEVANCE_STATUSES.IN_PROGRESS &&
      (inSuperUserMode() || user.isHso),
    [grievancePhaseData, inSuperUserMode, user.isHso, isActivePhase]
  );

  const canEditCommonFields = useMemo(
    () => isActivePhase && isEditable,
    [isActivePhase, isEditable]
  );

  const editableFields = useMemo(
    () =>
      new Map<string, boolean>([
        [GRIEVANCE_MILESTONES_CODES.AUTHORIZATION_OF_CLOSURE, canEditAuthClousureDate],
        [GRIEVANCE_MILESTONES_CODES.CLOSURE_NOTIFICATION, canEditCommonFields],
        ['clousureTypeId', canEditCommonFields],
        ['handledByMICIAfterwards', canEditCommonFields || alwaysEditable],
        ['additionalActions', canEditCommonFields],
        ['isClaimantMICIInformed', canEditCommonFields],
      ]),
    [canEditAuthClousureDate, canEditCommonFields, alwaysEditable]
  );

  const formik = useFormik<FormModel>({
    initialValues: {
      ...phase,
      ...grievancePhaseData,
      logbookItems: [],
    },
    validate: (values) => {
      if (grievancePhaseData.currentStatus !== GRIEVANCE_STATUSES.IN_PROGRESS) {
        return {};
      }

      const errors: FormikErrors<FormModel> = {};

      const selectedClosureType = lookupsClosureTypes.find(
        (ct) => ct.id.toString() === (values.closureTypeId || 0).toString()
      );

      const hasClosureNotificationDate = values.milestones.some(
        (m) => m.code === GRIEVANCE_MILESTONES_CODES.CLOSURE_NOTIFICATION && m.date
      );

      if (hasClosureNotificationDate && !selectedClosureType) {
        errors.closureTypeId = t('grievanceMilestoneValidations.closureTypeRequired');
      }

      values.milestones.forEach((m, i) => {
        if (m.date) {
          const selectedDate = new Date(m.date);
          const regDate = new Date(registrationDate);

          if (selectedDate < regDate) {
            setMilestoneError(
              errors,
              i,
              t('grievanceMilestoneValidations.minDate', { name: m.name })
            );
          } else {
            const prevMilestonesInCurrentPhase = values.milestones.findLast(
              (x) => x.generalOrder < m.generalOrder && x.date
            );

            const lastMilestoneInTimeline = phases
              .filter((p) => !p.isCurrent)
              .flatMap((p) => p.milestones)
              .findLast((ms) => ms.date && ms.generalOrder < m.generalOrder);

            const lastMilestoneCompleted = prevMilestonesInCurrentPhase || lastMilestoneInTimeline;

            if (
              lastMilestoneCompleted &&
              new Date(m.date) < new Date(lastMilestoneCompleted.date!)
            ) {
              const messageKey = prevMilestonesInCurrentPhase
                ? 'milestoneDateMustBeAfterMilestone'
                : 'milestoneDateMustBeAfterPrevious';

              setMilestoneError(
                errors,
                i,
                t(`grievanceMilestoneValidations.${messageKey}`, {
                  milestoneName: lastMilestoneCompleted.name,
                })
              );
            }
          }
        } else if (m.code === GRIEVANCE_MILESTONES_CODES.AUTHORIZATION_OF_CLOSURE) {
          if (selectedClosureType && selectedClosureType.authorizationOfClosureRequired) {
            setMilestoneError(
              errors,
              i,
              t('grievanceMilestoneValidations.authorizationOfClosureRequired', {
                milestoneName: phase.milestones[i].name,
              })
            );
          }
        }
      });

      if (selectedClosureType) {
        if (
          selectedClosureType.additionalActionsRequired &&
          (values.additionalActions === undefined || values.additionalActions === null)
        ) {
          errors.additionalActions = t('grievanceMilestoneValidations.additionalActionsRequired');
        }

        if (
          selectedClosureType.claimantInformedAboutMiciRequired &&
          (values.isClaimantMICIInformed === undefined || values.isClaimantMICIInformed === null)
        ) {
          errors.isClaimantMICIInformed = t(
            'grievanceMilestoneValidations.isClaimantMICIInformedRequired'
          );
        }
      }

      return errors;
    },

    enableReinitialize: true,
    onSubmit: () => {
      onPhaseApply(formik.values, {
        ...grievancePhaseData,
        additionalActions: formik.values.additionalActions,
        handledByMICIAfterwards: formik.values.handledByMICIAfterwards,
        isClaimantMICIInformed: formik.values.isClaimantMICIInformed,
        closureTypeId: formik.values.closureTypeId,
      });
    },
  });

  const closureTypesOptions: OptionValue[] = useMemo(
    () =>
      (lookupsClosureTypes || []).map((ct) => ({
        id: ct.id,
        text: ct.name,
        value: ct.id.toString(),
      })),
    [lookupsClosureTypes]
  );

  const buildMilestone = (milestonceCode: string) => {
    const milestoneIndex = phase.milestones.findIndex((m) => m.code === milestonceCode);
    if (milestoneIndex === -1) {
      return null;
    }

    const milestone = phase.milestones[milestoneIndex];

    const hasError =
      formik.errors &&
      formik.errors.milestones &&
      formik.errors.milestones[milestoneIndex] &&
      typeof formik.errors.milestones[milestoneIndex] === 'object' &&
      typeof formik.errors.milestones[milestoneIndex].date === 'string';

    return (
      <UIKitDatepicker
        name={`milestones[${milestoneIndex}].date`}
        // htmlId (no name, que es el path del campo de Formik) escopa el id/
        // aria-labelledby real del DOM por fase - sin esto, dos filas de fase
        // distintas expandidas a la vez terminan con el mismo id de input,
        // rompiendo aria-labelledby en la que se renderiza después (bug real
        // de accesibilidad, Fase 5/F7-EXIT).
        htmlId={`milestones[${phase.phaseId}][${milestoneIndex}].date`}
        label={milestone.name}
        selectedDate={
          formik.values.milestones[milestoneIndex].date
            ? new Date(formik.values.milestones[milestoneIndex].date)
            : null
        }
        onChange={(date) => formik.setFieldValue(`milestones[${milestoneIndex}].date`, date)}
        onBlur={formik.handleBlur}
        readonly={!editableFields.get(milestonceCode)}
        error={!!hasError}
        helperText={
          hasError
            ? (formik.errors.milestones![milestoneIndex] as FormikErrors<GrievanceMilestone>).date
            : null
        }
      />
    );
  };

  const buildBooleanDropdown = (fieldName: keyof FormModel, label: string) => {
    let disabledByClousureType = false;

    if (fieldName === 'isClaimantMICIInformed') {
      const selectedClosureType = lookupsClosureTypes.find(
        (ct) => ct.id.toString() === (formik.values.closureTypeId || 0).toString()
      );
      disabledByClousureType =
        !selectedClosureType || !selectedClosureType.claimantInformedAboutMiciRequired;
    }

    const editableField = editableFields.get(fieldName) || false;

    const error = formik.errors && formik.errors[fieldName];

    return (
      <UIKitDropDown
        label={label}
        elements={BOOLEAN_OPTIONS}
        value={boolToOptionId(formik.values[fieldName])}
        onChange={(id) => {
          const val = BOOLEAN_OPTIONS.find((o) => o.id === id);
          formik.setFieldValue(fieldName, val ? val.value === 'true' : false);
        }}
        disabled={!editableField || disabledByClousureType}
        className="grv-milestone-dropdown"
        error={!!error}
        helperText={error as string}
      />
    );
  };

  useEffect(() => {
    const selectedClosureType = lookupsClosureTypes.find(
      (ct) => ct.id.toString() === (formik.values.closureTypeId || 0).toString()
    );

    if (selectedClosureType && !selectedClosureType.claimantInformedAboutMiciRequired) {
      formik.setFieldValue('isClaimantMICIInformed', undefined, true);
    }
  }, [formik.values.closureTypeId]);

  useEffect(() => () => formik.resetForm(), [grievanceNumber]);

  return (
    <form className="grv-phases-form" onSubmit={formik.handleSubmit}>
      <Grid
        container
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'flex-start',
        }}
      >
        <Grid size={4}>{buildMilestone(GRIEVANCE_MILESTONES_CODES.AUTHORIZATION_OF_CLOSURE)}</Grid>
        <Grid size={4}>{buildMilestone(GRIEVANCE_MILESTONES_CODES.CLOSURE_NOTIFICATION)}</Grid>
        <Grid size={4}>
          <UIKitDropDown
            label={t('closureType')}
            elements={closureTypesOptions}
            value={formik.values.closureTypeId || 0}
            onChange={(id) => formik.setFieldValue('closureTypeId', id)}
            disabled={!editableFields.get('clousureTypeId')}
            className="grv-milestone-dropdown"
            error={!!formik.errors.closureTypeId}
            helperText={
              (formik.errors.closureTypeId ? formik.errors.closureTypeId : null) as string
            }
          />
        </Grid>
        <Grid size={4}>{buildBooleanDropdown('additionalActions', t('additionalActions'))}</Grid>
        <Grid size={4}>
          {buildBooleanDropdown('isClaimantMICIInformed', t('isClaimantMICIInformed'))}
        </Grid>
        <Grid size={4}>
          {buildBooleanDropdown('handledByMICIAfterwards', t('handledByMICIAfterwards'))}
        </Grid>
        {(canEditCommonFields || canEditAuthClousureDate || alwaysEditable) && (
          <Grid className="grv-milestone-submit-btn">
            <UIKitButton
              text={t('apply')}
              type="submit"
              className="btn-primary"
              disabled={!formik.isValid || !formik.dirty}
            />
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default ClosedMilestoneRow;
